//! PLEASE keep this list in alphabetical order for a better readability
export enum JOURNEY_ADMIN {
    /**
     * @value Authorized sending days
     * @description Title for section in journey's creation form where users can select days they want to receive journey items.
     *
     * Journey
     * A "journey" is some kind of "process" through which employees can go through. For example there can be an "onboarding" journey, where an employee gets its employee credentials, work computer, etc.
     *
     * Journey Item
     * Here an "item" can be a notification, a task, a meeting, that an employee receives or get assigned to.
     *
     */
    AUTHORIZED_SENDING_DAYS = 'JOURNEY_ADMIN.AUTHORIZED_SENDING_DAYS',
    /**
     * @value Journey items will only be sent on the selected days. If an item is scheduled for a restricted day, it will be delivered at the specified time on the next available workday.
     * @description Description for "authorized sending days" section
     */
    AUTHORIZED_SENDING_DAYS_DESCRIPTION = 'JOURNEY_ADMIN.AUTHORIZED_SENDING_DAYS_DESCRIPTION',
    /**
     * @value Select at least one sending day
     * @description Error message displayed when all sending days are disabled. It indicates the user that at least one sending day should be enabled to continue the creation or saving process.
     */
    AUTHORIZED_SENDING_DAYS_ERROR = 'JOURNEY_ADMIN.AUTHORIZED_SENDING_DAYS_ERROR',
    /**
     * @value The baseline date is used to calculate the sending date of the items to each employee.
     */
    BASELINE_DESCRIPTION = 'JOURNEY_ADMIN.BASELINE.DATE.DESCRIPTION',
    /**
     * @value Name the baseline date
     */
    BASELINE_PLACEHOLDER = 'JOURNEY_ADMIN.BASELINE.DATE.PLACEHOLDER',
    /**
     * @value The journey %NAME% will be permanently deleted. This action cannot be reverted.
     * @description "Journey" is a product name, it should not be translated
     */
    DELETE_CONFIRMATION_DESCRIPTION = 'JOURNEY_ADMIN.DELETE_CONFIRMATION_DESCRIPTION',
    /**
     * @value Delete journey?
     * @description "Journey" is a product name, it should not be translated
     */
    DELETE_CONFIRMATION_TITLE = 'JOURNEY_ADMIN.DELETE_CONFIRMATION_TITLE',
    /**
     * @value The stakeholder %STAKEHOLDER_TITLE% is currently assigned or referenced in the following items. Please assign another stakeholder before proceeding with the removal.
     * @description Explains why a specific stakeholder in a journey cannot be deleted.
     * A journey is an automated process that allows a user to send email and other messages (= items) to a group of users on specific dates.
     * A stakeholder in a person that is NOT enrolled in a journey but who is related to one or several enrolled users (as their manager, buddy or anything)
     */
    DELETE_STAKEHOLDER_DIALOG_MESSAGE = 'JOURNEY_ADMIN.DELETE_STAKEHOLDER_DIALOG_MESSAGE',
    /**
     * @value Impossible to delete this stakeholder
     * @description Title of a dialog that lets the user know why she cannot delete the stakeholder involved in a journey
     */
    DELETE_STAKEHOLDER_DIALOG_TITLE = 'JOURNEY_ADMIN.DELETE_STAKEHOLDER_DIALOG_TITLE',
    /**
     * @value Describe your journey
     * @description "journey" should not be translated, it is a product name, also, it is feminine.
     */
    DESCRIPTION_PLACEHOLDER = 'JOURNEY_ADMIN.DESCRIPTION.PLACEHOLDER',
    /**
     * @value No description
     * @description Empty message text to display when there is no description
     */
    EMPTY_DESCRIPTION = 'JOURNEY_ADMIN.EMPTY_DESCRIPTION',
    /**
     * @value No featured image
     * @description Text indicating that no featured image (thumbnail) has been uploaded for the current journey.
     */
    EMPTY_FEATURED_IMAGE = 'JOURNEY_ADMIN.EMPTY_FEATURED_IMAGE',
    /**
     * @value No stakeholders assigned
     * @description Message to display when there are no stakeholder, in the journey general view tab
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     *
     */
    EMPTY_STAKEHOLDERS = 'JOURNEY_ADMIN.EMPTY_STAKEHOLDERS',
    /**
     * @value There are no journeys
     * @description Journey is the name of the product and it mustn't be translated
     */
    EMPTY_STATE_TITLE = 'JOURNEY_ADMIN.EMPTY_STATE_TITLE',
    /**
     * @value Enrolled users
     */
    ENROLLED_USERS = 'ENROLLMENTS.ENROLLED_USERS',
    /**
     * @value Featured image
     */
    FEATURED_IMAGE = 'WIDGET_TYPE_FEATURED-IMAGE_PLACEHOLDER',
    /**
     * @value Add a featured image to increase the journey visibility and findability.
     * @description Description for "featured image" section
     */
    FEATURED_IMAGE_DESCRIPTION = 'JOURNEY_ADMIN.FEATURED_IMAGE_DESCRIPTION',
    /**
     * @value Items
     */
    ITEMS = 'ITEM_CONFIGURATIONS.ITEMS',
    /**
     * @value Journey created
     */
    JOURNEY_CREATED = 'JOURNEY_ADMIN.ALERT.CREATED',
    /**
     * @value Journey deleted
     * @description Notification message to display after the journey has been successfully deleted
     *
     * "Journey" is a product name, it should not be translated
     */
    JOURNEY_DELETED = 'JOURNEY_ADMIN.JOURNEY_DELETED',
    /**
     * @value Journey duplicated
     * @description It's an alert to be displayed in a toaster when the user tries to duplicate a specific journey. Shirt for "the journey has been duplicated"
     */
    JOURNEY_DUPLICATED = 'JOURNEY_ADMIN.DUPLICATED',
    /**
     * @value {"one":"%NUMBER% Item","other":"%NUMBER% Items"}
     * @description "item" is something that will be sent to users, it can be a notification or a task or a meeting, ...
     */
    JOURNEY_ITEMS = 'JOURNEY_ADMIN.ITEM',
    /**
     * @value Journey name
     * @description "journey" should not be translated, it is a product name, also, it is feminine.
     */
    NAME_LABEL = 'JOURNEY_ADMIN.NAME_LABEL',
    /**
     * @value Journey name (%LANG%)
     * @description "journey" should not be translated, it is a product name, also, it is feminine.
     */
    NAME_LABEL_WITH_LANG = 'JOURNEY_ADMIN.NAME_LABEL_WITH_LANG',
    /**
     * @value Name your journey
     * @description "journey" should not be translated, it is a product name, also, it is feminine.
     */
    NAME_PLACEHOLDER = 'JOURNEY_ADMIN.NAME.PLACEHOLDER',
    /**
     * @value New journey
     * @description "journey" should not be translated, it is a product name, also, it is feminine.
     */
    NEW_JOURNEY = 'JOURNEY_ADMIN.NEW_JOURNEY',
    /**
     * @value New stakeholder
     * @description Button to add a new stakeholder field.
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     */
    NEW_STAKEHOLDER = 'JOURNEY_ADMIN.NEW_STAKEHOLDER',
    /**
     * @value User profile field: %PROFILE_NAME%
     * @description display the user profile name (i.e: HR manager, software engineer etc)
     */
    PROFILE_NAME = 'JOURNEY_ADMIN.PROFILE_NAME',
    /**
     * @value Journeys settings
     * @description Title of the page where users can manage settings of the journey feature
     *
     * A "journey" is some kind of "process" through which employees can go through. For example there can be an "onboarding" journey, where an employee gets its employee credentials, work computer, etc.
     */
    SETTINGS = 'JOURNEY_ADMIN.SETTINGS',
    /**
     * @value All journeys
     * @description /!\ PLEASE DO NOT TRANSLATE THE WORD JOURNEYS! It's a feature's name that should be transcribed as is.
     *
     * Title of a button in a menu. Clicking it will display a page with all journeys.
     */
    SIDE_NAV_TITLE = 'JOURNEY_ADMIN.SIDE_NAV_TITLE',
    /**
     * @value Add stakeholders to the journey and assign them to your participants. Stakeholders can follow the progress of the participants through the journey and you can assign them task items during the journey.
     * @description Description to indicate the user what the current stakeholder section is for in the create journey form.
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     *
     * A "journey" is some kind of "process" through which employees can go through. For example there can be an "onboarding" journey, where an employee gets its employee credentials, work computer, etc.
     *
     * A participant is someone in the company who is part of a process, for example an onboarding process (ie when the employee joins the company).
     */
    STAKEHOLDER_DESCRIPTION = 'JOURNEY_ADMIN.STAKEHOLDER_DESCRIPTION',
    /**
     * @value Several stakeholders have the same label. Update them to submit the journey.
     * @description Error message displayed in a form of creation/edition of a journey. When the user adds stakeholders, if their labels are not unique (for example, 2 stakeholder are named "manager"), this error message is displayed
     */
    STAKEHOLDER_LABEL_ERROR = 'JOURNEY_ADMIN.STAKEHOLDER_LABEL_ERROR',
    /**
     * @value Name the stakeholder according to your journey scenario.
     * @description Helper to indicate to an user what is expected into the input.
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     *
     * A "journey" is some kind of "process" through which employees can go through. For example there can be an "onboarding" journey, where an employee gets its employee credentials, work computer, etc.
     */
    STAKEHOLDER_LABEL_HELPER = 'JOURNEY_ADMIN.STAKEHOLDER_LABEL_HELPER',
    /**
     * @value Maximum number of stakeholders reached (50).
     * @description Warning message indicating that the creation of stakeholders limit has been reached. When the message appears, it's impossible to create new stakeholders.
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     */
    STAKEHOLDER_MAX_LIMIT_MESSAGE = 'JOURNEY_ADMIN.STAKEHOLDER_MAX_LIMIT_MESSAGE',
    /**
     * @value Define the stakeholder from a user profile field. The user profile field is automatically assigned to each participant.
     * @description Helper text to be used below the picker of user profile field when selected a field as a journey stakeholder.
     *
     * A stakeholder is someone who is “linked” to an employee of the company, for example their Manager, or their HR Manager, or their teammate.
     */
    STAKEHOLDER_USERPROFILE_FIELD_HELPER = 'JOURNEY_ADMIN.STAKEHOLDER_USERPROFILE_FIELD_HELPER',
    /**
     * @value During edition, you cannot change the user profile field.
     * @description Helper message to let the user know why she cannot change the value of a specific field. She is in edit mode so the "user profile field" which is already selected cannot be changed
     */
    STAKEHOLDER_USERPROFILE_FIELD_NOT_EDITABLE = 'JOURNEY_ADMIN.STAKEHOLDER_USERPROFILE_FIELD_NOT_EDITABLE',
    /**
     * @value Manager
     * @description Example of a user profile field to be displayed in the user profile field picker to let the user know what kind of data can be displayed in the select.
     */
    STAKEHOLDER_USERPROFILE_FIELD_PLACEHOLDER = 'JOURNEY_ADMIN.STAKEHOLDER_USERPROFILE_FIELD_PLACEHOLDER',
    /**
     * @value Visible to journey participants.
     * @description Helper to indicate the user that the current field is visible to journey participants.
     *
     * A "journey" is some kind of "process" through which employees can go through. For example there can be an "onboarding" journey, where an employee gets its employee credentials, work computer, etc.
     */
    VISIBILITY_HELPER = 'JOURNEY_ADMIN.VISIBILITY_HELPER',
}
